import { router } from '@/app/router';
import { paths } from '@/config/paths';
import { openAssessment } from '@/features/assessments';
import heroBoyCelebrating from '@/features/hero/assets/avatar-celebrating.svg';
import heroBoySmiling from '@/features/hero/assets/avatar-smiling.svg';
import heroBoyUpset from '@/features/hero/assets/avatar-upset.svg';
import type {
  Cta,
  HeroStateConfig,
} from '@/features/hero/config/hero-states-v2';
import type { HeroData } from '@/features/hero/hooks';
import type { HeroStateCallbacks } from '@/features/hero/utils/hero-state-parser';
import { AssessmentType } from '@/lib/analytics/types';
import { getNumberToOrdinalName } from '@/lib/utils';

const makeCta = (heroData: HeroData, callbacks?: HeroStateCallbacks): Cta => {
  if (heroData.lastSession?.attendance?.status === 'MISSED') {
    return {
      variant: heroData.shouldShowSecondaryButton ? 'outline' : 'raised-jumbo',
      label: 'Watch Recording',
      onClick: () => {
        callbacks?.openRecordingDrawer?.();
      },
    };
  }
  const assessment = heroData?.latestIncompleteAssessment;
  const assessmentId = assessment?.assessmentId;
  if (assessmentId && assessment) {
    return {
      variant: heroData.shouldShowSecondaryButton ? 'outline' : 'raised-jumbo',
      label: 'Start Practice',
      onClick: () => {
        // Track the event before opening assessment
        if (callbacks?.trackEvent) {
          const assessmentType = assessment.assessmentCategoryType;

          callbacks.trackEvent('attemptAssessment', 1, {
            source: 'heroComponent',
            type: assessmentType.toLowerCase() as AssessmentType,
            reattempt: assessment.completionStatus ?? false,
            assessmentName: assessment.assessmentName ?? '',
          });
        }

        openAssessment(assessmentId);
      },
    };
  }
  return {
    variant: heroData.shouldShowSecondaryButton ? 'outline' : 'raised-jumbo',
    label: 'Start Practice',
    onClick: () => void router.navigate(paths.app.brainGym.getHref()),
  };
};

const makeStats = (): string => {
  return 'Keep up the consistency, your hard work pays off.';
};

const makeSubtitle = (heroData: HeroData): string => {
  if (heroData.latestIncompleteAssessment) {
    return `Let's lock in your learning — your next quiz is ready`;
  }
  return `You've completed all quizzes, now test yourself with fun practice questions!`;
};

const includeName = (heroData: HeroData): string => {
  if (heroData.studentName) {
    return ` ${heroData.studentName}`;
  }
  return '';
};

const HERO_STATES_V2_ONLINE: HeroStateConfig[] = [
  {
    id: 'SCHOOL_CURRICULUM',
    name: 'School Curriculum',

    isEnabled: heroData =>
      heroData.rawData.selectedCourse?.course_name
        ?.toLowerCase()
        .trim()
        .endsWith('academic maths') ?? false,

    hasError: heroData => heroData.errorStates.profile,

    getTitle: () => {
      return 'Watch Sessions of School Curriculum';
    },

    getDivider: () => true,

    getStats: () => "Your friends have been watching - now it's your turn!",

    getCta: (_, callbacks) => {
      return {
        variant: 'raised-jumbo',
        label: 'Watch Recordings',
        onClick: async () => {
          await callbacks?.navigate?.(paths.app.modules.getHref());
        },
      };
    },

    getCharacterImage: () => heroBoySmiling,
  },

  {
    id: 'STARTED_NEW_MODULE',
    name: 'Started New Module',

    isEnabled: heroData => {
      return heroData.hasNewModuleStarted;
    },

    hasError: heroData => {
      return (
        heroData.errorStates.modules || heroData.errorStates.pendingAssessments
      );
    },

    getTitle: heroData => {
      const moduleNumber = heroData.currentOngoingModule?.module.number;
      const ordinalModuleNumber = getNumberToOrdinalName(Number(moduleNumber));
      if (ordinalModuleNumber) {
        return `Great Going${includeName(heroData)}! Your <highlight>${ordinalModuleNumber} Module</highlight> Has Kicked Off`;
      }
      return `Great Going${includeName(heroData)}! Your <highlight>New Module</highlight> Has Kicked Off`;
    },

    getSubtitle: heroData => makeSubtitle(heroData),

    getStats: makeStats,

    getCta: (heroData, callbacks) => makeCta(heroData, callbacks),

    getCharacterImage: () => heroBoyCelebrating,
  },

  {
    id: 'ATTENDED_PREVIOUS_SESSION',
    name: 'Attended Previous Session',

    isEnabled: heroData => {
      return heroData.lastSession?.attendance?.status === 'ATTENDED';
    },

    hasError: heroData => {
      return (
        heroData.errorStates.modules || heroData.errorStates.pendingAssessments
      );
    },

    getTitle: heroData => {
      return `Great Job${includeName(heroData)}, Attending Your Session!`;
    },

    getSubtitle: heroData => makeSubtitle(heroData),

    getStats: makeStats,

    getCta: (heroData, callbacks) => makeCta(heroData, callbacks),

    getCharacterImage: () => heroBoySmiling,
  },

  {
    id: 'MISSED_1_SESSION',
    name: 'Missed 1 Session',

    isEnabled: heroData => {
      return heroData.lastSession?.attendance?.status === 'MISSED';
    },

    hasError: heroData => {
      return (
        heroData.errorStates.modules || heroData.errorStates.pendingAssessments
      );
    },

    getTitle: heroData => {
      return `Uh-Oh${includeName(heroData)}! You <highlight>Missed</highlight> A Session`;
    },

    getSubtitle: () => {
      return `Stay consistent by watching your missed session`;
    },

    getCta: (heroData, callbacks) => makeCta(heroData, callbacks),

    getCharacterImage: () => heroBoyUpset,
  },

  {
    id: 'DEFAULT',
    name: 'Default',

    isEnabled: () => true,

    getTitle: heroData => {
      return `Hi${includeName(heroData)}, Welcome to <highlight>Bhanzu Dashboard!</highlight>`;
    },

    getSubtitle: () => 'Your Bhanzu journey starts here',

    getCharacterImage: () => heroBoyCelebrating,
  },
];

export const DEFAULT_HERO_STATE_ONLINE: HeroStateConfig = {
  id: 'DEFAULT',
  name: 'Default',
  isEnabled: () => true,
  getTitle: () => 'Hi, Welcome to <highlight>Bhanzu Dashboard!</highlight>',
  getSubtitle: () => 'Your Bhanzu journey starts here',
  getCharacterImage: () => heroBoyCelebrating,
};

export default HERO_STATES_V2_ONLINE;
